import type { ChildrenProps } from '../../types';
import { cn } from '@workspace/utils';
import React, { Children, cloneElement, forwardRef, isValidElement, useId } from 'react';

interface ChildElementProps {
  id?: string;

  [key: string]: any;
}

export const Field = forwardRef<HTMLDivElement, ChildrenProps>(({ children, className }, ref) => {
  const id = useId(); // Generate a unique ID

  const processChild = (child: React.ReactNode) => {
    if (isValidElement(child)) {
      // Utiliser Object.assign au lieu de l'opérateur spread
      const newProps = Object.assign({}, child.props, { id }) as ChildElementProps;
      return cloneElement(child, newProps);
    }
    return child;
  };

  const processedChildren = Children.map(children, (child) => processChild(child));

  return (
    <div
      className={cn('space-y-1.5 w-full', className)}
      ref={ref}
    >
      {processedChildren}
    </div>
  );
});

Field.displayName = 'Field';

export interface LabelProps {
  children?: React.ReactNode;
  htmlFor?: string;
  id?: string;
  className?: string;
  required?: boolean;
}

export const Label = forwardRef<HTMLLabelElement, LabelProps>(({ children, htmlFor, id, className, required }, ref) => {
  return children ? (
    <label
      className={cn('text-xs inline-block', className)}
      htmlFor={htmlFor ?? id}
      ref={ref}
    >
      {children} {required ? <span className="text-red-500">*</span> : null}
    </label>
  ) : null;
});

Label.displayName = 'Label';

export const Description = forwardRef<HTMLDivElement, ChildrenProps>(({ children }, ref) => {
  if (!children) return null;
  return (
    <p
      className="text-xs text-gray-500 block"
      ref={ref}
    >
      {children}
    </p>
  );
});

Description.displayName = 'Description';

export const TextError = forwardRef<HTMLDivElement, ChildrenProps>(({ children, className }, ref) => {
  if (!children) return null;
  return (
    <p
      className={cn(className, 'text-xs bg-red-200 text-red-700 px-4 py-2 w-full rounded-md')}
      ref={ref}
    >
      {children}
    </p>
  );
});

TextError.displayName = 'TextError';
